import { DocOperations } from 'common/db/api/utils';
import { useCallback, useEffect, useState } from 'react';

export interface UseDocumentArgs<T extends object> {
	doc: DocOperations<T> | null;
}

export interface UseDocumentResult<T> {
	data: T | null;
	loading: boolean;
	error: string | null;
}

export const useDocument = <T extends object>(args: UseDocumentArgs<T>): UseDocumentResult<T> => {
	const { doc } = args;

	const [data, setData] = useState<{
		data: T | null;
		loading: boolean;
		error: string | null;
	}>({
		data: null,
		loading: true,
		error: null,
	});

	const setListener = useCallback(() => {
		setData((prev) => ({
			...prev,
			loading: true,
			error: null,
		}));
		if (!doc) return;
		return doc.listen(
			(result) => {
				setData((prev) => ({
					loading: false,
					error: null,
					data: result ?? null,
				}));
			},
			(err) => {
				setData({
					loading: false,
					error: err.message,
					data: null,
				});
			},
		);
	}, [doc]);

	useEffect(() => {
		const clearListener = setListener();
		return () => {
			clearListener?.();
		};
	}, [setListener]);

	return {
		data: data.data,
		loading: data.loading,
		error: data.error,
	};
};
